import { difference, get, isNil, isObject, keyBy, merge, omit, omitBy, pick, reduce, uniq, cloneDeep } from 'lodash';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';
import { sortingOrdersEnum } from 'constants/global';
import { QuestionType } from '@commutifi/models/Question';
import { REF_KEYS_LIST, SURVEY_INITIALIZATION_STEP, subQuestionIdPrefix } from 'constants/onboarding';
import { ascendingCompare, sortByKey } from 'utils/helpers';
import * as authTypes from 'store/modules/auth/types';
import types from './types';
import { getConditionals, applyModeConditionalModifier } from './conditionals';
import { filterInvalidQuestions, extractRouteDataAnswers } from './_utils';
import { deriveFollowUpQuestions, keepAnswersOfAskedFollowUps, removeAnswersOfUnaskedFollowUps } from './followUpQuestions';

/**
 * ---------------- REDUCER ----------------
 */
const initialState = {
  questions: [],
  status: {
    error: null,
    loaded: false,
    isLoading: false
  }
};

/**
 * Reducer state overview:
 *
 * enterpriseSurvey: {
 *  survey: {
 *    ..survey object from the db (referenceName, backgroundImage, directOnboarding, etc.)
 *    content: { ...translations content },
 *    questions: [...only ids],
 *    status: {
 *      error: used when we catch an error in a saga to propagate the error to the UI
 *      loaded: used by the UI to know the data is loaded
 *      isLoading: used by the UI to know if we are in a loading state
 *    }
 *  },
 *  questionsById: {
 *    [questionId]: {
 *      ...question like our table entity (order, section, conditionalMode, content: { ...translated content }, etc.)
 *    }
 *    ...All the other questions by id
 *  },
 *  prefilledData (AKA shortlink): {
 *    id: shortlink id
 *    building: if the survey is linked to a building
 *    account: if the survey is linked to a account
 *    enterprise: if the survey is linked to a enterprise
 *    organization: if the survey is linked to a organization
 *  },
 *  answersByStep: {
 *    [stepNumber] : {
 *      [reference_key || question.id]: { ...answer (could be any type) }
 *    },
 *    ... Other step answers
 *  }
 * }
 *
 */
const survey = (state = initialState, action = {}) => {
  switch (action.type) {
    case types.FETCH_SURVEY_REQUEST:
    case types.FETCH_ACCOUNT_DEFAULT_SURVEY_REQUEST:
      return {
        ...state,
        status: {
          ...state.status,
          error: null,
          isLoading: true
        }
      };
    case types.FETCH_SURVEY_SUCCESS:
    case types.FETCH_ACCOUNT_DEFAULT_SURVEY_SUCCESS:
      return {
        ...action.response.result.survey,
        status: {
          ...state.status,
          error: null,
          loaded: true,
          isLoading: false
        }
      };
    case types.FETCH_SURVEY_FAILURE:
    case types.FETCH_ACCOUNT_DEFAULT_SURVEY_FAILURE:
      return {
        ...initialState,
        status: {
          error: action.error,
          loaded: false,
          isLoading: false
        }
      };
    case types.RESET_SURVEY:
      return {
        questions: [],
        status: {
          error: null,
          loaded: false,
          isLoading: false
        }
      };
    default:
      return state;
  }
};
const questionsById = (state = {}, action = {}) => {
  switch (action.type) {
    case types.FETCH_SURVEY_SUCCESS:
    case types.FETCH_ACCOUNT_DEFAULT_SURVEY_SUCCESS:
      // Follow-up questions are not stored: they are derived from the answers (see followUpQuestions)
      return action.response.entities.questions || {};
    default:
      return state;
  }
};
const prefilledData = (state = {}, action = {}) => {
  switch (action.type) {
    case types.FETCH_SURVEY_SUCCESS:
      {
        const enterpriseSurveyOnly = omit(action.response.result, 'survey');
        const initialData = action.payload?.options?.initialData;
        return {
          ...merge({}, {
            accountContext: {
              ...state.accountContext
            }
          }, initialData),
          ...enterpriseSurveyOnly,
          isAddressUpdate: state.isAddressUpdate
        } || {};
      }
    // The default survey isn't opened from a share link, so a link opened earlier in the session must not linger
    case types.FETCH_ACCOUNT_DEFAULT_SURVEY_SUCCESS:
      return omit(state, 'shortlinkId');
    case types.SET_SURVEY_ACCOUNT_CONTEXT:
      return {
        ...state,
        accountContext: action.payload.accountContext
      };
    case authTypes.LOGOUT_SUCCESS:
    case authTypes.UNAUTHORIZE_USER:
    case authTypes.CLEAR_USER:
    case authTypes.CHANGE_USER:
      return {
        ...state,
        accountContext: null
      };
    default:
      return state;
  }
};
const defaultSurvey = (_state = {}, action = {}) => {
  switch (action.type) {
    case types.FETCH_ACCOUNT_DEFAULT_SURVEY_SUCCESS:
      return {
        isMandatory: action.response.result.isMandatory,
        surveyId: action.response.result.survey.id
      };
    default:
      return null;
  }
};
const answersByStep = (state = {}, action = {}) => {
  switch (action.type) {
    case types.UPDATE_SURVEY_ANSWERS:
      {
        /**
         * Since an account can answer multiple surveys we use existing commute routes to set initial
         * answers. These values like the frequency, time, etc. that can be extracted from the complete route object.
         */
        const {
          enhancedAnswers,
          frequencyAnswers,
          timeAnswers,
          commuteCosts,
          peopleCounts
        } = extractRouteDataAnswers(action.payload.answers);

        // Verify if we updated the enterpriseId question (to know if we need to reset the office id)
        // This is for when the office selector is not in the same section than the company selector.
        // Otherwise we are able to just reset the office selector when we update the company selected
        const getEnterpriseIdPreviousAnswer = () => get(state, `[${action.payload.stepNumber}].${REF_KEYS_LIST.accountEnterpriseId}`);
        const getEnterpriseIdUpdatedAnswer = () => action.payload.answers[REF_KEYS_LIST.accountEnterpriseId];
        // Compare by .key property to avoid false positives from object reference comparison
        // The enterprise ID answer is an object like { key: '2', label: 'Company Name' }
        const enterpriseIdUpdated = Object.keys(action.payload.answers).some(answerKey => answerKey === REF_KEYS_LIST.accountEnterpriseId && getEnterpriseIdPreviousAnswer()?.key !== getEnterpriseIdUpdatedAnswer()?.key);

        // Avoid using lodash.merge which also merged array values when we should overwrite them. Ex. commute days selection
        const updatedState = {
          ...state,
          [action.payload.stepNumber]: {
            ...state[action.payload.stepNumber],
            ...enhancedAnswers,
            ...commuteCosts,
            ...frequencyAnswers,
            ...timeAnswers,
            ...peopleCounts
          }
        };

        // If the enterprise id was updated we need to reset the office/building/worksite id in all sections that
        // contain them. We iterate all sections (not just the first match) in case these keys live in different sections.
        if (enterpriseIdUpdated) {
          Object.keys(updatedState).forEach(section => {
            const answers = updatedState[section];
            if (REF_KEYS_LIST.accountBuildingId in (answers || {}) || REF_KEYS_LIST.accountWorksiteId in (answers || {})) {
              updatedState[section] = {
                ...updatedState[section],
                [REF_KEYS_LIST.accountBuildingId]: null,
                [REF_KEYS_LIST.accountWorksiteId]: null
              };
            }
          });
        }
        return updatedState;
      }

    // --- NO DELETE action types are handled here ---
    // We keep all answers even when some questions are removed so we
    // can keep track of the answers. To get only the needed answers considering
    // conditionals we will get only the relevant question answers using a selector (see getCumulatedAnswers)
    case types.RESET_SURVEY_ANSWERS:
    case types.UPDATE_PROFILE_ADDRESS_DONE:
      return {
        [SURVEY_INITIALIZATION_STEP]: state?.[SURVEY_INITIALIZATION_STEP]
      };
    case authTypes.LOGOUT_SUCCESS:
    case authTypes.UNAUTHORIZE_USER:
    case authTypes.CLEAR_USER:
    case authTypes.CHANGE_USER:
      return {
        ...state,
        [SURVEY_INITIALIZATION_STEP]: null
      };
    default:
      return state;
  }
};

/**
 * ---------------- SELECTORS ----------------
 */
const emptyObject = {};
const _getQuestionsById = ({
  enterpriseSurvey
}) => enterpriseSurvey.questionsById;
const getIsLoading = ({
  enterpriseSurvey
}) => enterpriseSurvey.survey.status.isLoading;
const _getSurvey = ({
  enterpriseSurvey
}) => {
  return enterpriseSurvey.survey || {};
};
const getDefaultSurveyConfig = ({
  enterpriseSurvey
}) => {
  return enterpriseSurvey.defaultSurvey || {};
};
const makeGetSurvey = fields => createSelector([_getSurvey], survey => get(survey, fields));
const getSurveyId = createSelector([_getSurvey], survey => survey.id);
const getSurveyCustomBranding = ({
  enterpriseSurvey
}) => enterpriseSurvey.prefilledData.customBranding || emptyObject;
const getIsAnonymous = createSelector([_getSurvey], survey => survey.isAnonymous);
const getIsDirectOnboarding = createSelector([_getSurvey], survey => survey.directOnboarding);
const _getContent = createSelector([_getSurvey], survey => survey.content);
const getSuccessHeader = createSelector([_getContent], surveyContent => surveyContent?.successHeader);
const getSuccessContent = createSelector([_getContent], surveyContent => surveyContent?.successContent);
const makeGetContent = fields => createSelector([_getContent], content => get(content, fields));
const getIsLoaded = ({
  enterpriseSurvey
}) => enterpriseSurvey.survey.status.loaded;
const getError = ({
  enterpriseSurvey
}) => enterpriseSurvey.survey.status.error;
const getPrefilledData = ({
  enterpriseSurvey
}) => enterpriseSurvey.prefilledData;
const getIntroName = createSelector([getPrefilledData], prefilledData => get(prefilledData, 'organization.name') || get(prefilledData, 'enterprise.name'));
const getSurveyLogo = createSelector([getPrefilledData], prefilledData => get(prefilledData, 'organization.logo') || get(prefilledData, 'enterprise.logo'));
const makeGetPrefilledData = field => createSelector([getPrefilledData], prefilledData => get(prefilledData, field));
const _getAnswersByStep = ({
  enterpriseSurvey
}) => enterpriseSurvey.answersByStep;

/**
 * Follow-up questions (commute steps, secondary commute, kids drop-off) derived from the answers of
 * the questions they follow. See followUpQuestions for how and why.
 */
const _getFollowUpQuestions = createSelector([_getQuestionsById, _getAnswersByStep], deriveFollowUpQuestions);

// Sub-questions stored by an older version of the app (the store is kept in the session) are dropped:
// only the derived follow-ups reflect the current answers
const _getQuestionsByIdWithFollowUps = createSelector([_getQuestionsById, _getFollowUpQuestions], (questionsById, followUpQuestions) => ({
  ...omitBy(questionsById, question => question.id.startsWith(subQuestionIdPrefix)),
  ...keyBy(followUpQuestions, 'id')
}));

/**
 * The answers by step without the answers of follow-up questions that are no longer asked. Every selector
 * that reads answers uses this one, except makeGetSectionAnswers: a section form is still prefilled with
 * its previous answers if the commuter brings a follow-up question back.
 */
const _getAnswersByStepOfAskedQuestions = createSelector([_getAnswersByStep, _getFollowUpQuestions], keepAnswersOfAskedFollowUps);
const getQuestionsByIdOrRefKey = createSelector([_getQuestionsByIdWithFollowUps], questionsById => reduce(questionsById, (questionsByIdOrRefKey, question) => ({
  ...questionsByIdOrRefKey,
  [_extractQuestionId(question)]: question
}), {}));

/**
 * 1. Input
 *    _getAnswersByStep -> Answers organized by section number
 *    { [section1]: { [questionId]: { ...Answer } } }
 *
 * 2. Reduce the answers to flatten them out of each section number
 *
 * 3. Output
 *    { [question1]: { ...Answer 1 }, [question2]: { ...Answer 2 } }
 */
const _getAllAnswersById = createSelector([_getAnswersByStepOfAskedQuestions], answersByStep => {
  const {
    [SURVEY_INITIALIZATION_STEP]: initAnswers,
    ...otherSteps
  } = answersByStep;
  return {
    ...initAnswers,
    ...Object.values(otherSteps).reduce((acc, stepAnswers) => merge(acc, stepAnswers), {})
  };
});
const makeGetSectionAnswers = section => createSelector([_getAnswersByStep], answersByStep => answersByStep[section] || {});

/**
 * Get all survey questions considering the conditionals.
 * Ie. We could have a question 2 in the section 2 that should be shown
 *     only when question 1 has a specified value
 *
 * 1. Input multiple selectors to get specific parts of our state:
 *    _getAllAnswersById -> get the answers flatten out of the different
 *                          section numbers (see the selector comment for more details)
 *    questionsById      -> get the questions organized by id
 *    getPrefilledData   -> get the shortlink data to help us validate we have all information
 *                          needed to display or not a more complex component like
 *                          the office autocomplete that depends on the shortlink building and enterprise
 *
 * 2. Process the data:
 *    - separate questions that has conditional mode or is conditional to another question, etc.
 *    - filter out the invalid questions based on the type of component used for that question
 *      (ie. question.type = 'Commute Router' should be able to access a home and office address)
 *    - Sort by order so when we merge or compare questions from a section it is more processing friendly.
 *      (Because we will be able to add them in each section (when we need them by section number)
 *      with Array.push right away and they will already be properly sorted)
 *
 * 3. Output
 *    [{...question, order: 1, section: Y}, {...question, order: 1, section: X}, ...]
 */
export const _getQuestionsWithConditionals = createSelector([_getAllAnswersById, _getQuestionsByIdWithFollowUps, getPrefilledData, _getAnswersByStepOfAskedQuestions], (allAnswers, questionsById, enterpriseSurveyData, answersByStep) => {
  // Separate questions that has conditionals from questions that doesn't depend on
  // another question answer to check for conditional only on the relevant questions
  const conditionals = [];
  const otherQuestions = [];
  Object.values(questionsById).forEach(question => question.conditionalQuestionId || question.conditionalMode || question.conditionalCategory || !isNil(question.hideOnProfileUpdate) ? conditionals.push(question) : otherQuestions.push(question));
  const evaluableConditionals = applyModeConditionalModifier(conditionals, questionsById, answersByStep);
  const questions = [...getConditionals(evaluableConditionals, allAnswers, questionsById, enterpriseSurveyData), ...otherQuestions];
  const validQuestions = filterInvalidQuestions(questions, allAnswers, enterpriseSurveyData);
  return sortByKey(validQuestions, 'order', sortingOrdersEnum.ASC);
});

/**
 * Get all survey questions considering the conditionals.
 * Ie. We could have a question 2 in the section 2 that should be shown
 *     only when question 1 has a specified value
 *
 * 1. Input multiple selectors to get specific parts of our state:
 *    _getQuestionsWithConditionals -> array of ordered questions (by question.order property) considering the
 *                                     conditionals to filter all questions that should not be shown based on previous answers
 *
 * 2. Reduce the array to organize the questions by section number
 *
 * 3. Output
 *    {
 *      [section1]: [{...question, order: 1, section: 1}, {...question, order: 2, section: 1}, ...],
 *      [section2]: [{...question, order: 1, section: 2}, {...question, order: 2, section: 2}, ...],
 *      ...
 *    }
 *
 */
const getQuestionsBySectionWithConditionals = createSelector([_getQuestionsWithConditionals], questions => questions.reduce((questionsBySection, question) => ({
  ...questionsBySection,
  [question.section]: questionsBySection[question.section] ? questionsBySection[question.section].concat(question) : [question]
}), {}));

/**
 * Gets the ordered sections of the survey considering the conditional questions
 * that are not shown yet. This is important since we might have less sections shown
 * if a section contains ONLY questions conditional to previous answers
 *
 * WARNING: make sure we don't change this behaviour, otherwise we will have empty sections
 * and the app will crash
 *
 * _getQuestionsWithConditionals -> Get all questions that meets the conditional conditions
 */
const getOrderedSections = createSelector([_getQuestionsWithConditionals], questionsWithConditional => uniq(questionsWithConditional.map(question => +question.section)).sort(ascendingCompare));

/**
 * Get questions id given in the survey which is the reference key OR the id. If it
 * is the reference key we want to remove the whole question and sub-questions so
 * we take only the first level of data (thus the use of 'split()[0]')
 * @param {Question} question: question entity
 */
const _extractQuestionId = question => question.referenceKey ? Array.isArray(question.referenceKey) ? question.referenceKey[0] : question.referenceKey : question.id;

/**
 * Helper function to process answers by section with a configurable initial state.
 * This function contains the shared logic for both selectors.
 *
 * To have the most up to date answers we need to:
 * 1. Deeply merge each section answers with the previous answers.
 * 2. Get only the relevant question answers (we need to consider the conditional
 *    questions that don't need any answers)
 */
const _processCumulatedAnswers = (answersByStep, questionBySectionWithConditional, initialAnswers) => {
  const orderedSections = uniq(Object.keys(questionBySectionWithConditional || {}).map(section => parseFloat(section)).sort(ascendingCompare));
  return orderedSections.reduce(
  // merge => respect comment case #1
  // pick => respect comment case #2
  (cumulatedAnswers, section) => {
    const sectionQuestions = questionBySectionWithConditional[section];
    return merge(cumulatedAnswers, pick(answersByStep[section], sectionQuestions.map(_extractQuestionId)));
  }, initialAnswers);
};

/**
 * To have ALL cumulated answers with default answers from user's routes we need:
 * 1. Answers to all questions considering conditionals processed in _processCumulatedAnswers
 * 2. Include answers generated from the user's routes as initial state but overridden by the user's answers
 */
const getCumulatedAnswers = createSelector([_getAnswersByStepOfAskedQuestions, getQuestionsBySectionWithConditionals], (answersByStep, questionBySectionWithConditional) => {
  // We use the initialization step answers as the base to make sure these answers will be used as default answers
  // if no answer for those questions were set yet.
  // Deep clone fromCurrentAccount answers to prevent mutations
  const initialAnswers = answersByStep[SURVEY_INITIALIZATION_STEP] ? cloneDeep(answersByStep[SURVEY_INITIALIZATION_STEP]) : {};
  return _processCumulatedAnswers(answersByStep, questionBySectionWithConditional, initialAnswers);
});

/**
 * Same as getCumulatedAnswers but without using the initialization step answers that uses user's routes as default answers.
 * This is useful when you want to get only the user's explicit answers ONLY for the survey questions to submit only the relevant
 * answers to the API.
 */
const getSurveyFinalAnswers = createSelector([_getAnswersByStepOfAskedQuestions, getQuestionsBySectionWithConditionals], (answersByStep, questionBySectionWithConditional) => {
  return _processCumulatedAnswers(answersByStep, questionBySectionWithConditional, {});
});
const makeGetAnswer = (questionId, select = data => data) => createSelector([getCumulatedAnswers], answers => answers[questionId] ? select(answers[questionId]) : null);
const requiredQuestionsFilter = question => !question.optional && question.type !== QuestionType.statement;

/**
 * Get all REQUIRED questions (only those that are not optional, are not conditional to another question, etc.)
 * and return the unanswered questions alongside the section in which they belong to allow us to
 * redirect the user to the right section.
 *
 * 1. Input
 *    _getAnswersByStep -> Answers by section number
 *    getQuestionsBySectionWithConditionals -> { [section1]: [...questions of section 1], ... }
 *
 * 2. Process the data
 *    - Reduce on this object ({ [section1]: [...questions of section 1], ... }) with the second param
 *    defined like:
 *    (cumulated array of questions, array of questions for section X, section number X) => cumulated array of questions
 *    - Filter out all optional questions, get all answers ids that are the questions id
 *    - Get the difference between all the question ids of the current  reduce section and the
 *      answers to return only those that are unanswered
 *    - Map to add the section number with the question id
 *
 * 3. Output an array of unanswered question ids associated with the section
 *    [{ id: 'account_address', section: 1 }, ...]
 *
 * OPTIMIZATION possible. Get the ordered sections to be sure we start from the first one and return
 *                        as soon as we find an unanswered question in any section
 */
const getUnansweredQuestions = createSelector([_getAnswersByStepOfAskedQuestions, getQuestionsBySectionWithConditionals], (answersByStep, questionBySectionWithConditional) => reduce(questionBySectionWithConditional, (unansweredQuestions, sectionQuestions, section) => {
  // Get only required question reference key OR question id to find them in the answers and see if it was answered.
  // Statement questions can't be answered so we don't need to consider them even if they were not optional (probably a mistake).
  const questionIds = sectionQuestions.filter(requiredQuestionsFilter).map(_extractQuestionId);
  const sectionAnswers = answersByStep[section] || {};
  const answersIds = Object.keys(sectionAnswers).filter(answerId => {
    const answer = sectionAnswers[answerId];
    return !isObject(answer) && !isNil(answer) || Array.isArray(answer) || Object.values(answer || {}).length;
  });
  return [...unansweredQuestions, ...difference(questionIds, answersIds).map(qId => ({
    id: qId,
    section
  }))];
}, []));
const getPercentageOfCompletion = createSelector([getUnansweredQuestions, _getQuestionsWithConditionals], (unanswered, questions) => {
  const requiredQuestionCount = questions.filter(requiredQuestionsFilter).length;
  return (requiredQuestionCount - unanswered.length) / (requiredQuestionCount || Number.POSITIVE_INFINITY) * 100;
});

// New selector to get questionId by referenceKey
export const getQuestionIdForCommuteRouter = createSelector([_getQuestionsById], questionsById => {
  // Find the question with the specified referenceKey
  const question = Object.values(questionsById).find(q => q.referenceKey === REF_KEYS_LIST.commuteProfileRoute);

  // Return the questionId if found, otherwise return null
  return question ? question.id : null;
});
export const selectors = {
  getIsLoading,
  makeGetSurvey,
  makeGetContent,
  getIsLoaded,
  getError,
  getIntroName,
  getPrefilledData,
  makeGetPrefilledData,
  getQuestionsBySectionWithConditionals,
  makeGetSectionAnswers,
  getCumulatedAnswers,
  getSurveyFinalAnswers,
  makeGetAnswer,
  getSurveyId,
  getIsDirectOnboarding,
  getSuccessHeader,
  getSuccessContent,
  getSurveyLogo,
  getOrderedSections,
  getUnansweredQuestions,
  getQuestionsByIdOrRefKey,
  getIsAnonymous,
  getPercentageOfCompletion,
  getSurveyCustomBranding,
  getQuestionIdForCommuteRouter,
  getDefaultSurveyConfig
};
const enterpriseSurvey = combineReducers({
  survey,
  questionsById,
  prefilledData,
  answersByStep,
  defaultSurvey
});

// A new answer can make a follow-up question stop applying: its answer is deleted in the same update,
// which needs both the questions and the answers, so it cannot live in the answersByStep reducer alone
export default function enterpriseSurveyReducer(state, action) {
  const nextState = enterpriseSurvey(state, action);
  if (action.type !== types.UPDATE_SURVEY_ANSWERS) {
    return nextState;
  }
  const prunedAnswersByStep = removeAnswersOfUnaskedFollowUps(nextState.questionsById, nextState.answersByStep);
  return prunedAnswersByStep === nextState.answersByStep ? nextState : {
    ...nextState,
    answersByStep: prunedAnswersByStep
  };
}